/**
 * Custom postback domain — operator UI (FS-675).
 *
 * Agency-branded S2S callback hostname manager. A tenant registers
 * `postback.<brand>.com` so outbound S2S callback URLs land on their own
 * brand host instead of trackerity.com. DNS / TLS provisioning is out of
 * scope for this screen; the verify button is a toggle an operator flips
 * once the record is known-good.
 *
 * Layout:
 *   - Emerald -> sky gradient headline + sub-copy card.
 *   - Add-form card with inline validation + create button.
 *   - Domains table with a verified pill, Verify / Delete actions.
 *
 * Brand: emerald -> sky headline, zinc borders, 6-8px rounded, light only,
 * no emojis, no competitor names.
 */
import {
	Fragment,
	useCallback,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createDomain,
	customPostbackDomainQueryKeys,
	deleteDomain,
	formatUnixSec,
	listDomains,
	normalizeDomain,
	statusBadgeClass,
	statusLabel,
	statusOf,
	verifyDomain,
	type CustomPostbackDomain,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens (brand rules: emerald -> sky, zinc borders, 6-8px rounded).
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-rose-600";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface CustomPostbackDomainPageProps {
	/** Pre-populate the add-form input. Used for deep-link scenarios. */
	initialDomain?: string;
}

export function CustomPostbackDomainPage(
	props: CustomPostbackDomainPageProps = {},
) {
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<string>(props.initialDomain ?? "");
	const [draftError, setDraftError] = useState<string | null>(null);

	const domainsQuery = useQuery({
		queryKey: customPostbackDomainQueryKeys.list(),
		queryFn: listDomains,
	});

	const createMutation = useMutation({
		mutationFn: createDomain,
		onSuccess: (row: CustomPostbackDomain) => {
			toast.success(`Registered ${row.domain}.`);
			setDraft("");
			setDraftError(null);
			queryClient.invalidateQueries({
				queryKey: customPostbackDomainQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register domain.");
		},
	});

	const verifyMutation = useMutation({
		mutationFn: verifyDomain,
		onSuccess: (row: CustomPostbackDomain) => {
			toast.success(`Marked ${row.domain} as verified.`);
			queryClient.invalidateQueries({
				queryKey: customPostbackDomainQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to verify domain.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteDomain,
		onSuccess: () => {
			toast.success("Domain removed.");
			queryClient.invalidateQueries({
				queryKey: customPostbackDomainQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove domain.");
		},
	});

	const rows = domainsQuery.data ?? [];

	const handleSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const clean = normalizeDomain(draft);
			if (!clean) {
				setDraftError(
					"Enter a valid FQDN (e.g. postback.brand.com). Domains ending in trackerity.com are reserved.",
				);
				return;
			}
			setDraftError(null);
			createMutation.mutate(clean);
		},
		[draft, createMutation],
	);

	const submitDisabled =
		createMutation.isPending || draft.trim().length === 0;

	return (
		<main
			data-testid="custom-postback-domain-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<section
				aria-labelledby="add-domain-heading"
				className={`${CARD} flex flex-col gap-4 p-6`}
			>
				<div className="flex flex-col gap-1">
					<h2
						id="add-domain-heading"
						className="text-lg font-semibold text-zinc-900"
					>
						Add a branded postback domain
					</h2>
					<p className={HINT}>
						Register a hostname you control. Once DNS points to our
						infra, an operator can mark it verified below.
					</p>
				</div>

				<form onSubmit={handleSubmit} className="flex flex-col gap-3">
					<div className="flex flex-col gap-1">
						<label htmlFor="cpd-domain" className={LABEL}>
							Domain (FQDN)
						</label>
						<input
							id="cpd-domain"
							name="domain"
							type="text"
							inputMode="url"
							autoComplete="off"
							spellCheck={false}
							placeholder="postback.brand.com"
							className={INPUT}
							value={draft}
							onChange={(e) => setDraft(e.target.value)}
							disabled={createMutation.isPending}
							aria-invalid={draftError != null}
							aria-describedby={
								draftError ? "cpd-domain-error" : "cpd-domain-hint"
							}
						/>
						{draftError ? (
							<p id="cpd-domain-error" className={ERROR_TEXT}>
								{draftError}
							</p>
						) : (
							<p id="cpd-domain-hint" className={HINT}>
								Tenants may not register subdomains of trackerity.com.
							</p>
						)}
					</div>
					<div className="flex gap-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={submitDisabled}
						>
							{createMutation.isPending ? "Registering…" : "Register"}
						</button>
					</div>
				</form>
			</section>

			<section
				aria-labelledby="domains-heading"
				className={`${CARD} flex flex-col gap-4 p-6`}
			>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<h2
						id="domains-heading"
						className="text-lg font-semibold text-zinc-900"
					>
						Registered domains
					</h2>
					<span className={HINT}>
						{rows.length} {rows.length === 1 ? "domain" : "domains"}
					</span>
				</div>

				{domainsQuery.isLoading ? (
					<p className={HINT}>Loading…</p>
				) : domainsQuery.error ? (
					<p className={ERROR_TEXT}>
						{(domainsQuery.error as Error).message ||
							"Unable to load domains."}
					</p>
				) : rows.length === 0 ? (
					<p className={HINT}>
						No branded postback domains yet. Add one above.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[640px] border-collapse text-left text-sm">
							<thead>
								<tr className="border-b border-zinc-200 text-xs font-medium uppercase tracking-wide text-zinc-500">
									<th className="py-2 pr-4">Domain</th>
									<th className="py-2 pr-4">Status</th>
									<th className="py-2 pr-4">Registered</th>
									<th className="py-2 pr-4 text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row) => (
									<DomainRow
										key={row.id}
										row={row}
										onVerify={() => verifyMutation.mutate(row.id)}
										onDelete={() => {
											if (typeof window !== "undefined") {
												const ok = window.confirm(
													`Remove ${row.domain}?`,
												);
												if (!ok) return;
											}
											deleteMutation.mutate(row.id);
										}}
										busy={
											(verifyMutation.isPending &&
												verifyMutation.variables === row.id) ||
											(deleteMutation.isPending &&
												deleteMutation.variables === row.id)
										}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default CustomPostbackDomainPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Agency
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Custom postback domain
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Host S2S callback URLs on your own brand hostname (e.g.
				<span className="mx-1 rounded-[6px] border border-zinc-200 bg-zinc-50 px-1 py-0.5 font-mono text-xs text-zinc-800">
					postback.brand.com
				</span>
				). Point DNS at our infra, then flip verify once the record is
				live.
			</p>
		</header>
	);
}

interface DomainRowProps {
	row: CustomPostbackDomain;
	onVerify: () => void;
	onDelete: () => void;
	busy: boolean;
}

function DomainRow({ row, onVerify, onDelete, busy }: DomainRowProps) {
	const status = statusOf(row);
	const registered = useMemo(() => formatUnixSec(row.created_at), [row.created_at]);

	return (
		<tr
			data-testid={`cpd-row-${row.id}`}
			className="border-b border-zinc-100 last:border-b-0"
		>
			<td className="py-3 pr-4 font-mono text-zinc-900">{row.domain}</td>
			<td className="py-3 pr-4">
				<span className={statusBadgeClass(status)}>{statusLabel(status)}</span>
			</td>
			<td className="py-3 pr-4 text-zinc-600">{registered}</td>
			<td className="py-3 pr-0">
				<div className="flex items-center justify-end gap-2">
					{status === "verified" ? (
						<Fragment />
					) : (
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={onVerify}
							disabled={busy}
						>
							Verify
						</button>
					)}
					<button
						type="button"
						className={BTN_DANGER}
						onClick={onDelete}
						disabled={busy}
					>
						Delete
					</button>
				</div>
			</td>
		</tr>
	);
}
